import type { Client } from '@src/clients/domain/Client';
import { CollapsibleOrderSection } from '@src/orders/CollapsibleOrderSection';
import type { CheckedFields } from '@src/orders/domain/CheckedFields';
import {
  getCategoryFields,
  getSectionFields,
} from '@src/orders/domain/CheckedFields';
import type { Order } from '@src/orders/domain/Order';
import type { PermitInstruction } from '@src/orders/domain/PermitInstruction';
import {
  editOrderAccountsAndInsurancesEvents,
  editOrderAxleConfigurationEvents,
  editOrderCarrierAndPermitDeliveryEvents,
  editOrderHaulingEquipmentEvents,
  editOrderLoadInformationEvents,
  editOrderNotesAndDocumentsEvents,
  editOrderRoutingAndStatesEvents,
} from '@src/orders/EditOrderEvents';
import type { TabKey } from '@src/orders/EditOrderTabs';
import { OrderSection } from '@src/orders/OrderSection';
import { AccountSection } from '@src/orders/PermitFormSections/accountsAndInsurances/AccountSection';
import { InsuranceInformationSection } from '@src/orders/PermitFormSections/accountsAndInsurances/InsuranceInformationSection';
import { JurisdictionIdsSection } from '@src/orders/PermitFormSections/accountsAndInsurances/JurisdictionIdsSection';
import { AxleInformationSection } from '@src/orders/PermitFormSections/axlesConfiguration/AxleInformationSection';
import { OverallDimensionsSection } from '@src/orders/PermitFormSections/axlesConfiguration/OverallDimensionsSection';
import { CompanyAddressSection } from '@src/orders/PermitFormSections/carrierAndDelivery/CompanyAddressSection';
import { CompanyInformationSection } from '@src/orders/PermitFormSections/carrierAndDelivery/CompanyInformationSection';
import { DriverInformationSection } from '@src/orders/PermitFormSections/carrierAndDelivery/DriverInformationSection';
import { OrderInformationSection } from '@src/orders/PermitFormSections/carrierAndDelivery/OrderInformationSection';
import { PermitDeliveryAndNotificationsSection } from '@src/orders/PermitFormSections/carrierAndDelivery/PermitDeliveryAndNotificationsSection';
import { PONumberSection } from '@src/orders/PermitFormSections/carrierAndDelivery/PONumberSection';
import { HaulingEquipmentSection } from '@src/orders/PermitFormSections/haulingEquipment/HaulingEquipmentSection';
import { PermitsInstructionsSection } from '@src/orders/PermitFormSections/instructions/PermitsInstructionsSection';
import { LoadInformationSection } from '@src/orders/PermitFormSections/loadInformation/LoadInformationSection';
import { MobileHomeSection } from '@src/orders/PermitFormSections/loadInformation/MobileHomeSection';
import { Comments } from '@src/orders/PermitFormSections/notesAndDocuments/CommentsSection';
import { Documents } from '@src/orders/PermitFormSections/notesAndDocuments/DocumentsSection';
import { TripInformationSection } from '@src/orders/PermitFormSections/routingAndStates/TripInformationSection';
import type { TFunction } from 'i18next';
import type { ReactNode } from 'react';
import type { UseFormReturn } from 'react-hook-form';

interface TabPanel {
  category: TabKey;
  render: (
    order: Order,
    permitInstructions: PermitInstruction[],
    checkedFields: CheckedFields,
    client: Client,
    readonly: boolean,
    methods: UseFormReturn,
    t: TFunction,
  ) => ReactNode;
}

export const tabsPanels: TabPanel[] = [
  {
    category: 'instructions',
    render: (
      _order,
      permitInstructions,
      _checkedFields,
      _client,
      _readonly,
      _methods,
      t,
    ) => (
      <OrderSection title={t('orderSections.instructions')}>
        <PermitsInstructionsSection instructions={permitInstructions} />
      </OrderSection>
    ),
  },
  {
    category: 'carrierAndPermitDelivery',
    render: (
      _order,
      _permitInstructions,
      checkedFields,
      client,
      _readonly,
      _methods,
      t,
    ) => (
      <CollapsibleOrderSection
        title={t('orderSections.carrierAndPermitDelivery')}
        {...editOrderCarrierAndPermitDeliveryEvents}
      >
        <CompanyInformationSection
          {...editOrderCarrierAndPermitDeliveryEvents}
          fields={getSectionFields(
            'carrierAndPermitDelivery',
            'companyInformation',
            checkedFields,
          )}
        />
        <CompanyAddressSection
          {...editOrderCarrierAndPermitDeliveryEvents}
          fields={getSectionFields(
            'carrierAndPermitDelivery',
            'companyAddress',
            checkedFields,
          )}
        />
        <PONumberSection
          client={client}
          {...editOrderCarrierAndPermitDeliveryEvents}
        />
        <DriverInformationSection
          client={client}
          {...editOrderCarrierAndPermitDeliveryEvents}
          fields={getSectionFields(
            'carrierAndPermitDelivery',
            'driverInformation',
            checkedFields,
          )}
        />
        <OrderInformationSection
          {...editOrderCarrierAndPermitDeliveryEvents}
          fields={getSectionFields(
            'carrierAndPermitDelivery',
            'orderInformation',
            checkedFields,
          )}
        />
        <PermitDeliveryAndNotificationsSection
          client={client}
          {...editOrderCarrierAndPermitDeliveryEvents}
          fields={getSectionFields(
            'carrierAndPermitDelivery',
            'permitDeliveryAndNotifications',
            checkedFields,
          )}
        />
      </CollapsibleOrderSection>
    ),
  },
  {
    category: 'accountsAndInsurances',
    render: (
      _order,
      _permitInstructions,
      checkedFields,
      client,
      _readonly,
      _methods,
      t,
    ) => (
      <CollapsibleOrderSection
        title={t('orderSections.accountsAndInsurances')}
        {...editOrderAccountsAndInsurancesEvents}
      >
        <AccountSection
          {...editOrderAccountsAndInsurancesEvents}
          fields={getSectionFields(
            'accountsAndInsurances',
            'account',
            checkedFields,
          )}
        />
        <InsuranceInformationSection
          {...editOrderAccountsAndInsurancesEvents}
          client={client}
          fields={getSectionFields(
            'accountsAndInsurances',
            'insuranceInformation',
            checkedFields,
          )}
        />
        <JurisdictionIdsSection
          {...editOrderAccountsAndInsurancesEvents}
          fields={getSectionFields(
            'accountsAndInsurances',
            'juridictionIds',
            checkedFields,
          )}
        />
      </CollapsibleOrderSection>
    ),
  },
  {
    category: 'haulingEquipment',
    render: (
      _order,
      _permitInstructions,
      checkedFields,
      client,
      readonly,
      methods,
      t,
    ) => (
      <HaulingEquipmentSection
        title={t('orderSections.haulingEquipment')}
        clientId={client.id}
        methods={methods}
        readonly={readonly}
        withMetricToggle
        checkedFields={checkedFields}
        {...editOrderHaulingEquipmentEvents}
      />
    ),
  },
  {
    category: 'loadInformation',
    render: (
      _order,
      _permitInstructions,
      checkedFields,
      _client,
      _readonly,
      _methods,
      t,
    ) => (
      <CollapsibleOrderSection
        title={t('orderSections.loadInformation')}
        withMetricToggle
        {...editOrderLoadInformationEvents}
      >
        <LoadInformationSection
          {...editOrderLoadInformationEvents}
          fields={getSectionFields(
            'loadInformation',
            'loadInformation',
            checkedFields,
          )}
        />
        <MobileHomeSection
          {...editOrderLoadInformationEvents}
          fields={getSectionFields(
            'loadInformation',
            'mobileHome',
            checkedFields,
          )}
        />
      </CollapsibleOrderSection>
    ),
  },
  {
    category: 'axlesConfiguration',
    render: (
      _order,
      _permitInstructions,
      checkedFields,
      client,
      _readonly,
      _methods,
      t,
    ) => {
      return (
        <CollapsibleOrderSection
          title={t('orderSections.axlesConfiguration')}
          withMetricToggle
          {...editOrderAxleConfigurationEvents}
        >
          <AxleInformationSection
            clientId={client.id}
            fields={getSectionFields(
              'axlesConfiguration',
              'axles',
              checkedFields,
            )}
            order={_order}
            {...editOrderAxleConfigurationEvents}
          />
          <OverallDimensionsSection
            {...editOrderAxleConfigurationEvents}
            fields={getSectionFields(
              'axlesConfiguration',
              'overallDimensions',
              checkedFields,
            )}
          />
        </CollapsibleOrderSection>
      );
    },
  },
  {
    category: 'routingAndStates',
    render: (
      order,
      _permitInstructions,
      checkedFields,
      _client,
      readonly,
      _methods,
      t,
    ) => (
      <CollapsibleOrderSection
        title={t('orderSections.routingAndStates')}
        withMetricToggle
        {...editOrderRoutingAndStatesEvents}
      >
        <TripInformationSection
          applications={order.getAllLatestEffectivePermitApplicationsOrderedByIndex()}
          fields={[...getCategoryFields('routingAndStates', checkedFields)]}
          readonly={readonly}
          {...editOrderRoutingAndStatesEvents}
        />
      </CollapsibleOrderSection>
    ),
  },
  {
    category: 'notesAndDocuments',
    render: (
      order,
      _permitInstructions,
      checkedFields,
      _client,
      readonly,
      _methods,
      t,
    ) => (
      <CollapsibleOrderSection
        title={t('orderSections.notesAndDocuments')}
        {...editOrderNotesAndDocumentsEvents}
      >
        <Comments
          {...editOrderNotesAndDocumentsEvents}
          fields={getSectionFields(
            'notesAndDocuments',
            'comments',
            checkedFields,
          )}
        />
        <Documents
          {...editOrderNotesAndDocumentsEvents}
          orderId={order.id}
          readonly={readonly}
        />
      </CollapsibleOrderSection>
    ),
  },
];
